/**
 * Fait : l'écran « Mon compte » — qui l'on est tel que le serveur le dit,
 *   emporter ses données, et partir pour de bon.
 *
 *   Il a une ADRESSE (`#/compte`), et c'est pour cela qu'il est un écran et non
 *   un menu qui se déplie : un panneau flottant cache le contenu à fort zoom et
 *   se referme quand on ne veut pas, là où un lien se met en favori et se
 *   retrouve. Choix du plan `40.o`, contre la lettre du cadrage.
 *
 *   L'EXPORT EST UN LIEN ORDINAIRE, pas un appel : la réponse doit atterrir
 *   dans les téléchargements, pas être lue par la page. Ce que ça coûte est dit
 *   plus bas, à l'endroit où ça se paie.
 *
 *   LE DÉPART PASSE PAR UNE FENÊTRE DE CONFIRMATION, et par le mot de passe.
 *   Le cookie
 *   prouve qu'une session est ouverte, jamais que la personne devant l'écran
 *   est celle qui l'a ouverte — et un ordinateur familial reste allumé.
 *
 * Ne fait pas : il ne décide d'aucun droit. Le rôle qu'il montre vient du
 *   profil, il n'est pas déduit ici ; les deux refus de départ — compte
 *   fondateur, dernier parent administrateur — viennent du serveur et sont
 *   rendus tels quels, l'écran n'en invente ni n'en devine aucun.
 */

import { useState } from 'react';

import { Annonce } from '../gabarits/Annonce.tsx';
import { FenetreDeConfirmation } from '../gabarits/FenetreDeConfirmation.tsx';
import { Champ } from '../gabarits/Champ.tsx';
import { Partie } from '../gabarits/Partie.tsx';
import {
  adresseDeLExportDeMesDonnees,
  supprimeMonCompte,
  type BilanDeSuppressionVu,
  type ProfilVu,
} from './api.ts';
import { roleEnFrancais } from './roles.ts';

/**
 * CE QUE LE FICHIER CONTIENT, ET CE QU'IL NE CONTIENT PAS (`D-028`).
 *
 * L'export ne rend aujourd'hui que l'identité et les dates de connexion. Un
 * bouton nommé « emporter mes données » sans cette liste mentirait — le cas dit
 * par Charles : un fils qui part faire ses études et croit emporter trois mois
 * de travail. Enrichir l'export est une autre affaire ; le dire est celle-ci,
 * et les deux listes se corrigent ENSEMBLE le jour où il s'enrichit.
 */
const CE_QUE_LE_FICHIER_CONTIENT = [
  'votre adresse électronique et votre nom affiché',
  'votre rôle dans la famille',
  'la date de création de votre compte',
  'les dates de vos connexions',
];

const CE_QUE_LE_FICHIER_NE_CONTIENT_PAS = [
  'vos projets et leurs fiches',
  'vos conversations avec l’assistant',
  'les documents déposés',
  'les consignes que vous avez écrites',
];

/** Dit à la personne pourquoi on lui redemande ce qu'elle a déjà tapé en entrant. */
const POURQUOI_LE_MOT_DE_PASSE =
  'Il est demandé parce que le compte s’efface pour de bon, et qu’un ordinateur ' +
  'familial reste souvent allumé sans surveillance.';

export function PageCompte({
  profil,
  surDepart,
}: {
  readonly profil: ProfilVu;

  /** Appelé quand le compte n'existe plus : il n'y a plus de session à tenir. */
  surDepart: () => void;
}) {
  const [demande, setDemande] = useState(false);
  const [motDePasse, setMotDePasse] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [parti, setParti] = useState<BilanDeSuppressionVu | null>(null);

  const part = async () => {
    setRefus(null);
    setEnCours(true);

    const issue = await supprimeMonCompte(motDePasse);

    setEnCours(false);
    setMotDePasse('');

    if (issue.bon) {
      setParti(issue.valeur);
      return;
    }

    setRefus(issue.erreur.message);
  };

  /*
   * C'EST FAIT — et l'écran le dit AVANT de renvoyer à l'entrée. Renvoyer tout
   * de suite afficherait l'écran de connexion sans un mot, et quelqu'un qui
   * vient de supprimer son compte ne saurait pas s'il a été supprimé ou
   * simplement déconnecté. La session est déjà morte au serveur : ce qui reste
   * à l'écran n'est qu'un compte rendu.
   */
  if (parti !== null) {
    return (
      <Partie
        etiquette="Mon compte"
        titre="Votre compte est supprimé"
        nature="gens"
        niveauDeTitre={2}
        data-ecran="compte-supprime"
      >
        <Annonce urgence="urgent">
          Votre compte est supprimé. Vous n’êtes plus connecté à Clannik.
        </Annonce>

        <p data-role="bilan-de-suppression">
          Il restait {parti.avant.sessions} connexion(s) ouverte(s) et {parti.avant.droitsDeProjet}{' '}
          accès à des projets : il en reste {parti.apres.sessions} et {parti.apres.droitsDeProjet}.
        </p>

        <button type="button" data-role="revenir-a-l-entree" onClick={surDepart}>
          Revenir à l’entrée
        </button>
      </Partie>
    );
  }

  return (
    <>
      <Partie
        etiquette="Mon compte"
        titre="Qui vous êtes dans Clannik"
        nature="gens"
        niveauDeTitre={2}
        data-ecran="compte"
      >
        <dl data-role="identite">
          <dt>Nom affiché</dt>
          <dd data-role="nom-affiche">{profil.nomAffiche}</dd>

          <dt>Adresse électronique</dt>
          <dd data-role="adresse-electronique">{profil.adresseElectronique}</dd>

          <dt>Rôle</dt>
          <dd data-role="role">{roleEnFrancais(profil.role)}</dd>
        </dl>
      </Partie>

      <Partie
        etiquette="Mes données"
        titre="Emporter une copie de mes données"
        nature="travail"
        niveauDeTitre={2}
        aide="Un fichier à enregistrer sur votre ordinateur. Il ne supprime rien ici."
        data-ecran="export-de-mes-donnees"
      >
        <p data-role="ce-que-le-fichier-contient">
          Le fichier contient :{' '}
          {CE_QUE_LE_FICHIER_CONTIENT.join(', ').replace(/, ([^,]*)$/, ' et $1')}.
        </p>

        <p data-role="ce-que-le-fichier-ne-contient-pas" className="message-erreur">
          Il ne contient PAS :{' '}
          {CE_QUE_LE_FICHIER_NE_CONTIENT_PAS.join(', ').replace(/, ([^,]*)$/, ' ni $1')}.
        </p>

        <p className="texte-attenue" data-role="ou-se-pose-le-fichier">
          Le fichier se pose dans les téléchargements de cet ordinateur. S’il est partagé avec
          d’autres personnes, elles pourront l’y trouver.
        </p>

        <p>
          <a
            href={adresseDeLExportDeMesDonnees()}
            download
            data-role="emporter-mes-donnees"
            onClick={() => setDemande(true)}
          >
            Emporter mes données (fichier .json)
          </a>
        </p>

        {/*
         * L'annonce dit ce qui a été DEMANDÉ, jamais ce qui a réussi. Un lien
         * n'a aucun chemin d'erreur : la page ne saura pas si le serveur a
         * refusé. Écrire « votre fichier est prêt » serait une affirmation que
         * rien ne mesure — le genre de phrase qui laisse chercher pendant dix
         * minutes un fichier qui n'est jamais parti.
         */}
        {demande && (
          <Annonce urgence="poli">
            Le fichier a été demandé : regardez dans les téléchargements de votre navigateur. S’il
            n’y est pas, rien n’a été enregistré — réessayez.
          </Annonce>
        )}
      </Partie>

      <Partie
        etiquette="Partir"
        titre="Supprimer mon compte"
        nature="gens"
        niveauDeTitre={2}
        aide="Ce geste ne se rattrape pas : il n’y a pas de corbeille pour les comptes."
        data-ecran="suppression-de-mon-compte"
      >
        <p>
          Votre compte, vos connexions et vos accès aux projets seront effacés. Ce que vous avez
          écrit dans les projets partagés y reste, et les autres participants continueront de le
          voir : ce texte appartient au projet.
        </p>

        <form
          onSubmit={(evenement) => {
            // Le formulaire ne SOUMET pas : le geste passe par la fenêtre de
            // confirmation. Il existe pour que le champ soit un vrai champ de
            // formulaire — les gestionnaires de mots de passe ne remplissent
            // rien, sinon.
            evenement.preventDefault();
          }}
        >
          <Champ
            libelle="Votre mot de passe"
            aide={POURQUOI_LE_MOT_DE_PASSE}
            name="motDePasse"
            type="password"
            autoComplete="current-password"
            value={motDePasse}
            onChange={(evenement) => setMotDePasse(evenement.target.value)}
          />

          <FenetreDeConfirmation
            libelle="Supprimer mon compte"
            titre="Supprimer votre compte, définitivement ?"
            libelleDeConfirmation="Oui, supprimer mon compte"
            desactive={enCours || motDePasse === ''}
            surConfirmation={() => void part()}
            data-role="supprimer-mon-compte"
          >
            <p>
              Votre compte, vos connexions et vos accès aux projets seront effacés. Il n’y a pas de
              corbeille pour les comptes : rien ne ramène celui-ci.
            </p>
          </FenetreDeConfirmation>
        </form>

        {refus !== null && (
          <Annonce urgence="urgent" nature="refus">
            {refus}
          </Annonce>
        )}
      </Partie>
    </>
  );
}
